import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LOGO_URL, BRAND } from "./brand";
import { useAuth } from "./AuthContext";
import { LogOut, User, Instagram, Menu, X } from "lucide-react";
import { InstallAppButton } from "./components/InstallAppButton";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const doLogout = async () => { await logout(); nav("/"); };

  const link = ({ isActive }) =>
    `px-3 py-2 rounded-full text-sm font-display transition ${
      isActive ? "text-[color:var(--sun-700)]" : "text-[color:var(--ink-700)] hover:text-[color:var(--sun-600)]"
    }`;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[rgba(251,246,236,0.78)] border-b border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt={BRAND.name} className="w-11 h-11 object-contain" />
            <div className="leading-tight">
              <div className="font-serif text-xl tracking-widest text-[color:var(--ink-900)]">ARYA</div>
              <div className="font-display text-[10px] tracking-[0.35em] text-[color:var(--sun-600)]">STUDIO</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" end className={link} data-testid="nav-home">Início</NavLink>
            <NavLink to="/servicos" className={link} data-testid="nav-services">Especialidades</NavLink>
            <NavLink to="/galeria" className={link} data-testid="nav-gallery">Galeria</NavLink>
            <NavLink to="/agendar" className={link} data-testid="nav-book">Agendar</NavLink>
            <NavLink to="/parcerias" className={link} data-testid="nav-partners">Parcerias</NavLink>
            <NavLink to="/seja-parceiro" className={link} data-testid="nav-be-partner">Seja Parceiro</NavLink>
            <NavLink to="/reclamar" className={link} data-testid="nav-complaint">Ouvidoria</NavLink>
            <NavLink to="/indique" className={link} data-testid="nav-referrals">Indique</NavLink>
            {user?.role && ["admin","admin_admin","admin_caixa"].includes(user.role) && <NavLink to="/admin" className={link} data-testid="nav-admin">Painel</NavLink>}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <InstallAppButton className="btn-ghost !py-2 !px-4 text-sm" testid="nav-install-app"/>
            {user ? (
              <>
                <Link to="/perfil" className="flex items-center gap-2 text-sm font-display text-[color:var(--ink-700)] hover:text-[color:var(--sun-700)]" data-testid="nav-profile">
                  <User size={16} /> {user.name?.split(" ")[0] || "Perfil"}
                </Link>
                <button onClick={doLogout} className="btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="nav-logout">
                  <LogOut size={14} /> Sair
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm font-display text-[color:var(--ink-700)] hover:text-[color:var(--sun-700)]" data-testid="nav-login">Entrar</Link>
                <Link to="/cadastro" className="btn-gold !py-2 !px-5 text-sm" data-testid="nav-register">Criar conta</Link>
              </>
            )}
          </div>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-[var(--line)] px-5 py-3 flex flex-col gap-2 bg-[rgba(251,246,236,0.95)]">
            <NavLink onClick={() => setOpen(false)} to="/" end className={link}>Início</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/servicos" className={link}>Especialidades</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/galeria" className={link}>Galeria</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/agendar" className={link}>Agendar</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/parcerias" className={link}>Parcerias</NavLink>
            <NavLink onClick={() => setOpen(false)} to="/indique" className={link}>Indique</NavLink>
            {user?.role && ["admin","admin_admin","admin_caixa"].includes(user.role) && <NavLink onClick={() => setOpen(false)} to="/admin" className={link}>Painel</NavLink>}
            <InstallAppButton className="btn-ghost !py-2 !px-4 text-sm w-fit" testid="nav-mobile-install-app"/>
            {user ? (
              <>
                <Link onClick={() => setOpen(false)} to="/perfil" className={link({isActive:false})}>Meu perfil</Link>
                <button onClick={() => { doLogout(); setOpen(false); }} className="btn-ghost !py-2 !px-4 text-sm w-fit">Sair</button>
              </>
            ) : (
              <>
                <Link onClick={() => setOpen(false)} to="/login" className={link({isActive:false})}>Entrar</Link>
                <Link onClick={() => setOpen(false)} to="/cadastro" className="btn-gold !py-2 !px-5 text-sm w-fit">Criar conta</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-24 border-t border-[var(--line)]">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src={LOGO_URL} alt="" className="w-10 h-10 object-contain" />
              <div>
                <div className="font-serif text-lg tracking-widest">ARYA STUDIO</div>
                <div className="font-script text-[color:var(--sun-600)]">{BRAND.tagline}</div>
              </div>
            </div>
          </div>
          <div>
            <h4 className="font-display text-xs tracking-[0.3em] text-[color:var(--sun-700)] mb-3">NAVEGAÇÃO</h4>
            <ul className="space-y-2 text-sm text-[color:var(--ink-700)]">
              <li><Link to="/servicos">Especialidades</Link></li>
              <li><Link to="/agendar">Agendar horário</Link></li>
              <li><Link to="/indique">Indique e Ganhe</Link></li>
              <li><Link to="/parcerias">Parcerias</Link></li>
              <li><InstallAppButton className="hover:text-[color:var(--sun-700)]" label="Baixar o app" testid="footer-install-app"/></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-xs tracking-[0.3em] text-[color:var(--sun-700)] mb-3">CONTATO</h4>
            <a href="https://wa.me/5521992483417" target="_blank" rel="noreferrer" className="block text-sm text-[color:var(--ink-700)] hover:text-[color:var(--sun-700)]" data-testid="footer-phone">
              +55 21 99248-3417
            </a>
            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-700)] hover:text-[color:var(--sun-700)] mt-2">
              <Instagram size={16}/> {BRAND.instagram}
            </a>
            <p className="text-xs text-[color:var(--ink-500)] mt-4">© {new Date().getFullYear()} Arya Studio. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
